import { useEffect, useMemo, useState } from 'react';
import { DISCIPLINE_PALETTE, type Discipline } from '@obraplan/core/domain/structure/discipline';
import {
  DEFAULT_BELOW_GROUND_NAME_PATTERN,
  DEFAULT_FLOOR_NAME_PATTERN,
  planFloorRange,
  type PlannedFloor,
} from '@obraplan/core/domain/structure/floor';
import type { StructureTree } from '@obraplan/core/domain/structure/structure-tree';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field, Input } from '@/components/ui/input';
import { cn } from '@/lib/cn';
import {
  alternarDisciplina,
  alternarPavimento,
  chaveDaLigacao,
  colunasDaEstrutura,
  ligacoesExistentes,
  lotesParaAplicar,
  type ColunaDoPavimento,
  type LoteDeLigacao,
} from './grade-de-disciplinas';
import type { RenameTarget } from './tree-model';

// ── Nova torre ──────────────────────────────────────────────────────────────────

export function CreateTowerDialog({
  open,
  onOpenChange,
  onSubmit,
  isPending,
  errorMessage,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (name: string) => void;
  isPending: boolean;
  errorMessage: string | undefined;
}) {
  const [name, setName] = useState('');

  useEffect(() => {
    if (open) setName('');
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Nova torre</DialogTitle>
          <DialogDescription>
            Uma torre existe uma única vez na obra. Os pavimentos vêm em seguida.
          </DialogDescription>
        </DialogHeader>
        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            onSubmit(name);
          }}
        >
          <Field label="Nome da torre" htmlFor="tower-name" required error={errorMessage}>
            <Input
              id="tower-name"
              autoFocus
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="Torre Única"
            />
          </Field>
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" variant="primary" disabled={isPending || name.trim() === ''}>
              {isPending ? 'Criando…' : 'Criar torre'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── Pavimentos em sequência ─────────────────────────────────────────────────────

export function CreateFloorRangeDialog({
  open,
  onOpenChange,
  towerName,
  onSubmit,
  isPending,
  errorMessage,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  towerName: string;
  onSubmit: (input: FloorRangeSubmit) => void;
  isPending: boolean;
  errorMessage: string | undefined;
}) {
  const [fromLevel, setFromLevel] = useState(0);
  const [toLevel, setToLevel] = useState(3);
  const [namePattern, setNamePattern] = useState(DEFAULT_FLOOR_NAME_PATTERN);
  const [belowGroundPattern, setBelowGroundPattern] = useState(DEFAULT_BELOW_GROUND_NAME_PATTERN);

  useEffect(() => {
    if (open) {
      setFromLevel(0);
      setToLevel(3);
      setNamePattern(DEFAULT_FLOOR_NAME_PATTERN);
      setBelowGroundPattern(DEFAULT_BELOW_GROUND_NAME_PATTERN);
    }
  }, [open]);

  // O modelo de baixo só aparece quando a sequência desce abaixo do térreo — e só então
  // é enviado, para que quem não tem subsolo não veja um campo que não faz nada.
  const goesBelowGround = fromLevel < 0;

  // Pré-visualização calculada pela **mesma função** que o caso de uso usa para gravar.
  const planned = planFloorRange({
    fromLevel,
    toLevel,
    namePattern,
    ...(goesBelowGround ? { belowGroundPattern } : {}),
  });
  const preview: PlannedFloor[] = planned.ok ? planned.value : [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Criar pavimentos em sequência</DialogTitle>
          <DialogDescription>
            Em {towerName}. Pavimentos que já existem são mantidos, não recriados.
          </DialogDescription>
        </DialogHeader>

        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            onSubmit({
              fromLevel,
              toLevel,
              namePattern,
              ...(goesBelowGround ? { belowGroundPattern } : {}),
            });
          }}
        >
          <div className="grid grid-cols-2 gap-3">
            <Field
              label="Do nível"
              htmlFor="from-level"
              required
              hint="0 = Térreo · negativo = abaixo dele"
            >
              <Input
                id="from-level"
                type="number"
                value={fromLevel}
                onChange={(event) => setFromLevel(Number(event.target.value))}
              />
            </Field>
            <Field label="Até o nível" htmlFor="to-level" required>
              <Input
                id="to-level"
                type="number"
                value={toLevel}
                onChange={(event) => setToLevel(Number(event.target.value))}
              />
            </Field>
          </div>

          <Field
            label={goesBelowGround ? 'Acima do térreo' : 'Modelo do nome'}
            htmlFor="name-pattern"
            required
            hint="Use {n} no lugar do número. O nível 0 é sempre o Térreo."
          >
            <Input
              id="name-pattern"
              value={namePattern}
              onChange={(event) => setNamePattern(event.target.value)}
            />
          </Field>

          {goesBelowGround && (
            <Field
              label="Abaixo do térreo"
              htmlFor="below-ground-pattern"
              required
              hint="{n} é a profundidade: 1 é o andar logo abaixo do térreo. Ex.: G{n}."
            >
              <Input
                id="below-ground-pattern"
                value={belowGroundPattern}
                onChange={(event) => setBelowGroundPattern(event.target.value)}
              />
            </Field>
          )}

          <div className="rounded-md border border-(--color-border-subtle) bg-(--color-surface-muted) p-3">
            <p className="mb-2 text-xs font-medium uppercase tracking-wide text-(--color-content-muted)">
              Serão criados {preview.length} {preview.length === 1 ? 'pavimento' : 'pavimentos'}
            </p>
            {planned.ok ? (
              <div className="flex flex-wrap gap-1.5">
                {preview.slice(0, 12).map((floor) => (
                  <Badge key={floor.level} tone="neutral">
                    {floor.name}
                  </Badge>
                ))}
                {preview.length > 12 && <Badge tone="neutral">+{preview.length - 12}</Badge>}
              </div>
            ) : (
              <p className="text-sm text-(--color-state-danger)">{planned.error.message}</p>
            )}
          </div>

          {errorMessage !== undefined && (
            <p role="alert" className="text-sm text-(--color-state-danger)">
              {errorMessage}
            </p>
          )}

          <DialogFooter>
            <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={
                isPending ||
                !planned.ok ||
                preview.length === 0 ||
                namePattern.trim() === '' ||
                (goesBelowGround && belowGroundPattern.trim() === '')
              }
            >
              {isPending ? 'Criando…' : `Criar ${String(preview.length)}`}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export interface FloorRangeSubmit {
  readonly fromLevel: number;
  readonly toLevel: number;
  readonly namePattern: string;
  /** Só vai quando a sequência desce abaixo do térreo (RN-239). */
  readonly belowGroundPattern?: string;
}

// ── Novo pavimento ──────────────────────────────────────────────────────────────

export interface FloorSubmit {
  readonly towerId: string;
  readonly name: string;
  readonly level: number;
  readonly code: string | undefined;
}

/**
 * Um pavimento só, com nome e nível livres (RN-238).
 *
 * É o caminho do andar que não se repete e não se numera: **Fundação**, **Contenção**,
 * **Preliminar** — a etapa da obra que vira lugar abaixo do térreo (ADR-141) —, e também o
 * mezanino ou a casa de máquinas. A sequência continua sendo o caminho dos andares que se
 * contam.
 */
export function CreateFloorDialog({
  open,
  onOpenChange,
  towers,
  onSubmit,
  isPending,
  errorMessage,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  towers: readonly { id: string; name: string }[];
  onSubmit: (input: FloorSubmit) => void;
  isPending: boolean;
  errorMessage: string | undefined;
}) {
  const [towerId, setTowerId] = useState('');
  const [name, setName] = useState('');
  const [level, setLevel] = useState(-1);
  const [code, setCode] = useState('');

  useEffect(() => {
    if (open) {
      setTowerId(towers[0]?.id ?? '');
      setName('');
      setLevel(-1);
      setCode('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- só ao abrir
  }, [open]);

  const towerName = towers.find((tower) => tower.id === towerId)?.name ?? '';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Novo pavimento</DialogTitle>
          <DialogDescription>
            {towers.length > 1 ? 'Escolha a torre, o nome e o nível.' : `Em ${towerName}.`} O nível
            só ordena: o nome é o que a obra usa.
          </DialogDescription>
        </DialogHeader>

        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            onSubmit({
              towerId,
              name,
              level,
              code: code.trim() === '' ? undefined : code.trim(),
            });
          }}
        >
          {towers.length > 1 && (
            <Field label="Torre" htmlFor="floor-tower" required>
              <select
                id="floor-tower"
                className="h-11 w-full rounded-md border border-(--color-border-subtle) bg-(--color-surface) md:h-9 px-3 text-sm"
                value={towerId}
                onChange={(event) => setTowerId(event.target.value)}
              >
                {towers.map((tower) => (
                  <option key={tower.id} value={tower.id}>
                    {tower.name}
                  </option>
                ))}
              </select>
            </Field>
          )}

          <Field label="Nome do pavimento" htmlFor="floor-name" required error={errorMessage}>
            <Input
              id="floor-name"
              autoFocus
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="Fundação"
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field
              label="Nível"
              htmlFor="floor-level"
              required
              hint="0 = Térreo · negativo = abaixo dele"
            >
              <Input
                id="floor-level"
                type="number"
                value={level}
                onChange={(event) => setLevel(Number(event.target.value))}
              />
            </Field>
            <Field label="Código" htmlFor="floor-code" hint="Opcional.">
              <Input
                id="floor-code"
                value={code}
                onChange={(event) => setCode(event.target.value)}
              />
            </Field>
          </div>

          <DialogFooter>
            <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={
                isPending || towerId === '' || name.trim() === '' || !Number.isInteger(level)
              }
            >
              {isPending ? 'Criando…' : 'Criar pavimento'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── Aplicar disciplinas em lote ─────────────────────────────────────────────────

/**
 * A grade pavimento × disciplina.
 *
 * Eram duas listas que nasciam **todas marcadas**: para ligar cinco disciplinas a doze
 * pavimentos era preciso desmarcar trinta e tantas, uma a uma, sem ver o que cada andar
 * já tinha. Na grade, cada célula é uma ligação: a que já existe aparece preenchida, a nova
 * aparece marcada, e o título da linha ou da coluna marca a fileira inteira.
 *
 * Só aparecem disciplinas **ativas** — a desativada saiu de uso, e o servidor também a
 * recusa. As colunas podem ser cortadas por um intervalo de pavimentos, e é sobre o que
 * está à vista que o título da linha age: "PILARES do 1º ao 12º" são dois cliques.
 */
export function ApplyDisciplinesDialog({
  open,
  onOpenChange,
  tree,
  disciplines,
  onSubmit,
  isPending,
  errorMessage,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tree: StructureTree;
  disciplines: readonly Discipline[];
  onSubmit: (lotes: readonly LoteDeLigacao[]) => void;
  isPending: boolean;
  errorMessage: string | undefined;
}) {
  const [selecao, setSelecao] = useState<ReadonlySet<string>>(new Set());
  const [busca, setBusca] = useState('');
  const [de, setDe] = useState('');
  const [ate, setAte] = useState('');

  const colunas = useMemo(() => colunasDaEstrutura(tree), [tree]);
  const existentes = useMemo(() => ligacoesExistentes(tree), [tree]);
  const ativas = useMemo(
    () =>
      disciplines
        .filter((discipline) => discipline.isActive && discipline.deletedAt === undefined)
        .sort((a, b) => a.position - b.position || a.name.localeCompare(b.name, 'pt-BR')),
    [disciplines],
  );

  useEffect(() => {
    if (!open) return;
    // Começa vazia: o que já existe está à vista, e o que é novo é escolha de quem abriu.
    setSelecao(new Set());
    setBusca('');
    setDe(colunas[0]?.id ?? '');
    setAte(colunas[colunas.length - 1]?.id ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps -- só ao abrir
  }, [open]);

  const inicio = Math.max(
    0,
    colunas.findIndex((coluna) => coluna.id === de),
  );
  const fimAchado = colunas.findIndex((coluna) => coluna.id === ate);
  const fim = fimAchado === -1 ? colunas.length - 1 : fimAchado;
  const visiveis = colunas.slice(Math.min(inicio, fim), Math.max(inicio, fim) + 1);
  const idsVisiveis = visiveis.map((coluna) => coluna.id);

  const procurado = busca.trim().toLocaleLowerCase('pt-BR');
  const linhas = ativas.filter(
    (discipline) =>
      procurado === '' || discipline.name.toLocaleLowerCase('pt-BR').includes(procurado),
  );
  const idsDasLinhas = linhas.map((discipline) => discipline.id);

  const alternar = (chave: string) =>
    setSelecao((atual) => {
      const proxima = new Set(atual);
      if (proxima.has(chave)) proxima.delete(chave);
      else proxima.add(chave);
      return proxima;
    });

  const andaresComNovidade = new Set([...selecao].map((chave) => chave.split('|')[0]));
  // Várias torres: o nome da torre sobe para uma faixa acima dos pavimentos dela.
  const torres = visiveis.reduce<{ nome: string; quantos: number }[]>((grupos, coluna) => {
    const ultimo = grupos[grupos.length - 1];
    if (ultimo?.nome === coluna.torre) ultimo.quantos += 1;
    else grupos.push({ nome: coluna.torre, quantos: 1 });
    return grupos;
  }, []);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl">
        <DialogHeader>
          <DialogTitle>Aplicar disciplinas aos pavimentos</DialogTitle>
          <DialogDescription>
            Clique na célula para ligar a disciplina ao pavimento. O nome da disciplina liga ela em
            todos os pavimentos à vista; o nome do pavimento liga todas as disciplinas à vista.
            Célula preenchida é ligação que já existe.
          </DialogDescription>
        </DialogHeader>

        {colunas.length === 0 ? (
          <p className="text-sm text-(--color-content-muted)">
            Nenhum pavimento cadastrado. Monte a estrutura primeiro.
          </p>
        ) : (
          <div className="flex flex-col gap-3">
            <div className="flex flex-wrap items-end gap-3">
              <div className="min-w-52 flex-1">
                <Field label="Procurar disciplina" htmlFor="grade-busca">
                  <Input
                    id="grade-busca"
                    placeholder="Ex.: alvenaria"
                    value={busca}
                    onChange={(event) => setBusca(event.target.value)}
                  />
                </Field>
              </div>
              <Field label="Pavimentos de" htmlFor="grade-de">
                <select
                  id="grade-de"
                  className={SELECT_CLASS}
                  value={de}
                  onChange={(event) => setDe(event.target.value)}
                >
                  {colunas.map((coluna) => (
                    <option key={coluna.id} value={coluna.id}>
                      {rotuloDoAndar(coluna, colunasDeTorres(colunas) > 1)}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="até" htmlFor="grade-ate">
                <select
                  id="grade-ate"
                  className={SELECT_CLASS}
                  value={ate}
                  onChange={(event) => setAte(event.target.value)}
                >
                  {colunas.map((coluna) => (
                    <option key={coluna.id} value={coluna.id}>
                      {rotuloDoAndar(coluna, colunasDeTorres(colunas) > 1)}
                    </option>
                  ))}
                </select>
              </Field>
              <Button
                variant="ghost"
                disabled={selecao.size === 0}
                onClick={() => setSelecao(new Set())}
              >
                Limpar seleção
              </Button>
            </div>

            {linhas.length === 0 ? (
              <p className="text-sm text-(--color-content-muted)">
                {ativas.length === 0
                  ? 'Nenhuma disciplina ativa. Ative ou cadastre em Configurações → Disciplinas.'
                  : 'Nenhuma disciplina ativa corresponde à busca.'}
              </p>
            ) : (
              <div className="max-h-[26rem] overflow-auto rounded-md border border-(--color-border-subtle)">
                <table className="border-separate border-spacing-0 text-sm">
                  <thead className="sticky top-0 z-20 bg-(--color-surface-raised)">
                    {torres.length > 1 && (
                      <tr>
                        <th className="sticky left-0 z-30 bg-(--color-surface-raised)" />
                        {torres.map((torre, indice) => (
                          <th
                            key={`${torre.nome}-${String(indice)}`}
                            colSpan={torre.quantos}
                            className="border-b border-l border-(--color-border-subtle) px-1 py-1 text-xs font-semibold"
                          >
                            {torre.nome}
                          </th>
                        ))}
                      </tr>
                    )}
                    <tr>
                      <th
                        scope="col"
                        className="sticky left-0 z-30 min-w-52 border-b border-(--color-border-subtle) bg-(--color-surface-raised) px-3 py-2 text-left text-xs font-semibold text-(--color-content-muted)"
                      >
                        Disciplina
                      </th>
                      {visiveis.map((coluna) => (
                        <th
                          key={coluna.id}
                          scope="col"
                          className="border-b border-l border-(--color-border-subtle) p-0"
                        >
                          <button
                            type="button"
                            title={`Ligar todas as disciplinas à vista em ${coluna.torre} · ${coluna.nome}`}
                            className={cn(
                              'flex h-10 w-14 items-center justify-center px-1 text-center text-xs font-medium leading-tight hover:bg-(--color-surface-muted)',
                              andaresComNovidade.has(coluna.id) && 'text-(--color-brand)',
                            )}
                            onClick={() =>
                              setSelecao((atual) =>
                                alternarPavimento(atual, coluna.id, idsDasLinhas, existentes),
                              )
                            }
                          >
                            <span className="line-clamp-2">{nomeCurto(coluna.nome)}</span>
                          </button>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {linhas.map((discipline) => {
                      const novas = idsVisiveis.filter((floorId) =>
                        selecao.has(chaveDaLigacao(floorId, discipline.id)),
                      ).length;
                      return (
                        <tr key={discipline.id} className="group">
                          <th
                            scope="row"
                            className="sticky left-0 z-10 border-b border-(--color-border-subtle) bg-(--color-surface-raised) p-0 text-left font-normal group-hover:bg-(--color-surface-muted)"
                          >
                            <button
                              type="button"
                              title={`Ligar ${discipline.name} em todos os pavimentos à vista`}
                              className="flex w-full items-center gap-2 px-3 py-1.5 text-left hover:text-(--color-brand)"
                              onClick={() =>
                                setSelecao((atual) =>
                                  alternarDisciplina(atual, discipline.id, idsVisiveis, existentes),
                                )
                              }
                            >
                              <span
                                className="size-2.5 shrink-0 rounded-full"
                                style={{ backgroundColor: discipline.color ?? 'currentColor' }}
                                aria-hidden="true"
                              />
                              <span className="min-w-0 flex-1 truncate">{discipline.name}</span>
                              {novas > 0 && (
                                <span className="tabular text-xs text-(--color-brand)">
                                  +{novas}
                                </span>
                              )}
                            </button>
                          </th>
                          {visiveis.map((coluna) => {
                            const chave = chaveDaLigacao(coluna.id, discipline.id);
                            const existe = existentes.has(chave);
                            const marcada = selecao.has(chave);
                            return (
                              <td
                                key={coluna.id}
                                className="border-b border-l border-(--color-border-subtle) p-0 text-center group-hover:bg-(--color-surface-muted)"
                              >
                                <button
                                  type="button"
                                  disabled={existe}
                                  aria-pressed={existe || marcada}
                                  aria-label={`${discipline.name} em ${coluna.torre} · ${coluna.nome}${existe ? ' — já ligada' : ''}`}
                                  title={
                                    existe
                                      ? 'Já ligada. Para tirar, remova a atividade na árvore.'
                                      : undefined
                                  }
                                  className="flex h-8 w-14 items-center justify-center disabled:cursor-default"
                                  onClick={() => alternar(chave)}
                                >
                                  <span
                                    aria-hidden="true"
                                    className={cn(
                                      'flex size-5 items-center justify-center rounded border text-[11px] font-semibold',
                                      existe
                                        ? 'border-transparent bg-(--color-content-muted) text-(--color-surface)'
                                        : marcada
                                          ? 'border-(--color-brand) bg-(--color-brand) text-(--color-brand-contrast)'
                                          : 'border-(--color-border-subtle) bg-(--color-surface)',
                                    )}
                                  >
                                    {existe ? '✓' : marcada ? '+' : ''}
                                  </span>
                                </button>
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        <p className="mt-3 text-sm text-(--color-content-muted)" aria-live="polite">
          {selecao.size === 0 ? (
            'Nenhuma ligação nova marcada.'
          ) : (
            <>
              <strong className="text-(--color-content)">{selecao.size}</strong>{' '}
              {selecao.size === 1 ? 'ligação nova' : 'ligações novas'} em {andaresComNovidade.size}{' '}
              {andaresComNovidade.size === 1 ? 'pavimento' : 'pavimentos'}.
            </>
          )}
        </p>

        {errorMessage !== undefined && (
          <p role="alert" className="mt-2 text-sm text-(--color-state-danger)">
            {errorMessage}
          </p>
        )}

        <DialogFooter>
          <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            disabled={isPending || selecao.size === 0}
            onClick={() => onSubmit(lotesParaAplicar(selecao))}
          >
            {isPending ? 'Aplicando…' : 'Aplicar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const SELECT_CLASS =
  'h-11 rounded-md border border-(--color-border-subtle) bg-(--color-surface) px-2 text-sm md:h-9';

function colunasDeTorres(colunas: readonly ColunaDoPavimento[]): number {
  return new Set(colunas.map((coluna) => coluna.torre)).size;
}

function rotuloDoAndar(coluna: ColunaDoPavimento, comTorre: boolean): string {
  return comTorre ? `${coluna.torre} · ${coluna.nome}` : coluna.nome;
}

/** "1º Pavimento" cabe numa coluna estreita como "1º"; o nome inteiro fica no `title`. */
function nomeCurto(nome: string): string {
  return nome.replace(/\s*pavimento\s*/i, ' ').trim() || nome;
}

// ── Renomear torre ou pavimento ─────────────────────────────────────────────────

export interface RenameSubmit {
  readonly name: string;
  readonly code: string | undefined;
  /** Só é enviado para pavimento. */
  readonly level: number | undefined;
}

/**
 * Renomear é a única edição estrutural que existe.
 *
 * A identidade da atividade — pavimento × disciplina — é imutável (RN-008), então a
 * atividade não aparece aqui: para trocá-la, remove-se e cria-se outra. O pavimento
 * também edita o nível, porque é ele que ordena a árvore e um nível errado só se percebe
 * quando "10º" aparece antes do "2º".
 */
export function RenameNodeDialog({
  open,
  onOpenChange,
  target,
  onSubmit,
  isPending,
  errorMessage,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  target: RenameTarget | undefined;
  onSubmit: (input: RenameSubmit) => void;
  isPending: boolean;
  errorMessage: string | undefined;
}) {
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [level, setLevel] = useState(0);

  useEffect(() => {
    if (open && target !== undefined) {
      setName(target.name);
      setCode(target.code ?? '');
      setLevel(target.level ?? 0);
    }
  }, [open, target]);

  if (target === undefined) return null;

  const isFloor = target.type === 'FLOOR';
  const noun = isFloor ? 'pavimento' : 'torre';
  const unchanged =
    name.trim() === target.name &&
    code.trim() === (target.code ?? '') &&
    (!isFloor || level === target.level);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Renomear {noun}</DialogTitle>
          <DialogDescription>
            {isFloor
              ? 'O nome é único dentro da torre. O nível define a ordem na árvore.'
              : 'O nome é único dentro da obra.'}
          </DialogDescription>
        </DialogHeader>

        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            onSubmit({
              name,
              code: code.trim() === '' ? undefined : code.trim(),
              level: isFloor ? level : undefined,
            });
          }}
        >
          <Field
            label={`Nome ${isFloor ? 'do pavimento' : 'da torre'}`}
            htmlFor="rename-name"
            required
            error={errorMessage}
          >
            <Input
              id="rename-name"
              autoFocus
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </Field>

          {isFloor && (
            <Field
              label="Nível"
              htmlFor="rename-level"
              required
              hint="0 = Térreo · negativo = abaixo dele"
            >
              <Input
                id="rename-level"
                type="number"
                value={level}
                onChange={(event) => setLevel(Number(event.target.value))}
              />
            </Field>
          )}

          <Field label="Código" htmlFor="rename-code" hint="Opcional — uso interno da obra.">
            <Input
              id="rename-code"
              value={code}
              onChange={(event) => setCode(event.target.value)}
            />
          </Field>

          <DialogFooter>
            <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={isPending || name.trim() === '' || unchanged}
            >
              {isPending ? 'Salvando…' : 'Salvar'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── Nova disciplina ─────────────────────────────────────────────────────────────

export interface DisciplineSubmit {
  readonly name: string;
  readonly code: string | undefined;
  readonly color: string;
  /** `true` = catálogo do sistema, disponível para todas as obras (RN-007). */
  readonly global: boolean;
  /** Unidade típica do serviço — as frentes desta disciplina nascem com ela. */
  readonly unitOfMeasureId: string | undefined;
}

/**
 * Nova disciplina.
 *
 * O alcance é escolha explícita, e o padrão é **a obra**: "Impermeabilização" numa reforma
 * pontual não precisa aparecer no catálogo das outras. Quem trabalha com a mesma
 * disciplina em toda obra marca "todas as obras" e a cadastra uma vez só.
 *
 * A opção global só aparece para quem tem papel de sistema — disciplina global afeta
 * obras que a pessoa talvez nem alcance (RN-133). Esconder não é autorizar: o caso de uso
 * recusa de qualquer forma (RN-134).
 *
 * A cor é apoio visual na árvore, nunca o único indicador: o nome está sempre escrito.
 */
export function CreateDisciplineDialog({
  open,
  onOpenChange,
  projectName,
  canCreateGlobal,
  units,
  onSubmit,
  isPending,
  errorMessage,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectName: string;
  canCreateGlobal: boolean;
  /** Catálogo de unidades para a escolha da medição. */
  units: readonly { id: string; name: string }[];
  onSubmit: (input: DisciplineSubmit) => void;
  isPending: boolean;
  errorMessage: string | undefined;
}) {
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [color, setColor] = useState(DISCIPLINE_PALETTE[0]!);
  const [isGlobal, setIsGlobal] = useState(false);
  const [unitId, setUnitId] = useState('');

  useEffect(() => {
    if (open) {
      setName('');
      setCode('');
      setColor(DISCIPLINE_PALETTE[0]!);
      setIsGlobal(false);
      setUnitId('');
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Nova disciplina</DialogTitle>
          <DialogDescription>
            {isGlobal
              ? 'Fica disponível em todas as obras, agora e nas que vierem depois.'
              : `Fica disponível apenas em ${projectName}.`}
          </DialogDescription>
        </DialogHeader>

        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            onSubmit({
              name,
              code: code.trim() === '' ? undefined : code.trim(),
              color,
              global: isGlobal,
              unitOfMeasureId: unitId === '' ? undefined : unitId,
            });
          }}
        >
          <Field label="Nome da disciplina" htmlFor="discipline-name" required error={errorMessage}>
            <Input
              id="discipline-name"
              autoFocus
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="Impermeabilização"
            />
          </Field>

          <Field label="Código" htmlFor="discipline-code" hint="Opcional — uso interno da obra.">
            <Input
              id="discipline-code"
              value={code}
              onChange={(event) => setCode(event.target.value)}
            />
          </Field>

          {canCreateGlobal && (
            <Field
              label="Alcance"
              htmlFor="discipline-scope"
              hint="Disciplina de obra não polui o catálogo das outras."
            >
              <select
                id="discipline-scope"
                className="h-11 w-full rounded-md border border-(--color-border-subtle) bg-(--color-surface) md:h-9 px-2 text-sm"
                value={isGlobal ? 'GLOBAL' : 'PROJECT'}
                onChange={(event) => setIsGlobal(event.target.value === 'GLOBAL')}
              >
                <option value="PROJECT">Somente {projectName}</option>
                <option value="GLOBAL">Todas as obras</option>
              </select>
            </Field>
          )}

          {/*
            A unidade fica na disciplina para as frentes nascerem medidas: escolher m²
            duzentas vezes depois de uma importação é o caminho mais curto para ninguém
            medir nada. Cada atividade pode trocar depois.
          */}
          <Field
            label="Unidade de medição"
            htmlFor="discipline-unit"
            hint="As frentes desta disciplina nascem medidas assim. Deixe em branco para não medir."
          >
            <select
              id="discipline-unit"
              className="h-11 w-full rounded-md border border-(--color-border-subtle) bg-(--color-surface) md:h-9 px-2 text-sm"
              value={unitId}
              onChange={(event) => {
                setUnitId(event.target.value);
              }}
            >
              <option value="">Sem medição</option>
              {units.map((unit) => (
                <option key={unit.id} value={unit.id}>
                  {unit.name}
                </option>
              ))}
            </select>
          </Field>

          <fieldset className="flex flex-col gap-2">
            <legend className="text-sm font-medium">Cor na árvore</legend>
            <div className="flex flex-wrap gap-2">
              {DISCIPLINE_PALETTE.map((option) => (
                <button
                  key={option}
                  type="button"
                  aria-label={`Cor ${option}`}
                  aria-pressed={color === option}
                  onClick={() => setColor(option)}
                  className={cn(
                    'size-8 rounded-full border-2 transition-transform',
                    color === option
                      ? 'border-(--color-content) scale-110'
                      : 'border-transparent hover:scale-105',
                  )}
                  style={{ backgroundColor: option }}
                />
              ))}
            </div>
          </fieldset>

          <DialogFooter>
            <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" variant="primary" disabled={isPending || name.trim() === ''}>
              {isPending ? 'Criando…' : 'Criar disciplina'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
